import type { Nullable } from '@/type-utils';
import { errorToDTO } from '@/utils/error-utils';
import { SESSION_ID_KEY, USER_ID_KEY } from '@/configs/env/public';
import Service from '../../Service';
import { EnvironmentService } from '../EnvironmentService';

import CurrentRequestService from '../CurrentRequestService';
import { LogLevel } from './LogLevel';
import LoggerServiceMock from './LoggerServiceMock';

/** Defines valid log message types. */
export type Loggable = string | Error;

/** Context extracted from the current server-side request for structured logs. */
interface IRequestContext {
  /** Short unique ID generated per request for correlation. */
  requestID: Nullable<string>;
  /** The `deckersSID` session cookie value, if available. */
  sessionID?: string;
  /** The `deckersUserID` user cookie value, if available. */
  userID?: string;
}

/** Abstracts logging for the project. */
export class LoggerService extends Service {
  /**
   * Gets request context for structured logs: request ID, session ID
   * (`deckersSID`), and user ID (`deckersUserID`).
   *
   * @returns Context from the current request, or defaults if unavailable.
   */
  private getRequestContext(): IRequestContext {
    if ((typeof window === "undefined") && CurrentRequestService.inRequest) {
      const [req] = CurrentRequestService.tryGet();
      const requestID = req?.id ?? '(Unable to get request ID)';
      const rawSID = req?.cookies?.get(SESSION_ID_KEY);
      // Truncate to first 8 chars to prevent session hijacking via log access.
      // TODO: Hash with a dedicated LOG_HASH_SECRET env var for stronger
      // protection.
      const sessionID = rawSID?.slice(0, 8);
      const userID = req?.cookies?.get(USER_ID_KEY);

      return { requestID, sessionID, userID };
    }

    return { requestID: null };
  }

  /**
   * Log a message and optionally specify log level.
   * @param message - The message string or exception to be logged.
   * @param [level=info] - Level of this log.
   */
  public log(message: Loggable, level: LogLevel = LogLevel.Info): void {
    const { requestID, sessionID, userID } = this.getRequestContext();

    /** The object that will be output into the logs containing the message and metadata. */
    const logObject = {
      ...// If the "message" is an error, convert it to a DTO of the Error instance.
      (message instanceof Error ? { error: errorToDTO(message) } : { message }),
      level,
      timestamp: new Date().toISOString(),
      requestID: requestID ?? undefined,
      sessionID,
      userID
    };

    /** The log object serialized to a JSON string and pretty-printed. */
    const logObjectJSON = JSON.stringify(logObject, null, 2);

    // Log the message at the specified level.
    switch (level) {
      case LogLevel.Error:
        console.error(logObjectJSON);
        break;
      case LogLevel.Warn:
        console.warn(logObjectJSON);
        break;
      case LogLevel.Info:
        console.info(logObjectJSON);
        break;
      case LogLevel.Debug:
        console.debug(logObjectJSON);
        break;
    }
  }

  /**
   * Logs a message with an "error" log level.
   * @param message - The message string or exception to be logged.
   */
  public error(message: Loggable): void {
    this.log(message, LogLevel.Error);
  }

  /**
   * Logs a message with a "warn" log level.
   * @param message - The message string or exception to be logged.
   */
  public warn(message: Loggable): void {
    this.log(message, LogLevel.Warn);
  }

  /**
   * Logs a message with an "info" log level.
   * @param message - The message string or exception to be logged.
   */
  public info(message: Loggable): void {
    this.log(message, LogLevel.Info);
  }

  /**
   * Logs a message with a "debug" log level.
   * @param message - The message string or exception to be logged.
   */
  public debug(message: Loggable): void {
    this.log(message, LogLevel.Debug);
  }
}

export default LoggerService.withMock(
  new LoggerServiceMock(LoggerService)
) as unknown as LoggerService;
